import React, { useState } from 'react'
import { connect } from 'react-redux'
import {
    Card,
    CardContent,
    Typography,
    makeStyles,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Snackbar,
} from '@material-ui/core'
import Alert from '@mui/material/Alert'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import { useIntl } from 'react-intl'

import OrganizationOverviewSidebar from '../Organization/Components/OrganizationOverviewSidebar'
import { CapeeshColors } from '../../../assets/ColorPalette'
import { i18nMessages } from '../../../Logic/CodeAccessible.i18n.Messages'
import { userService } from '../../../_state/services'

const useStyles = makeStyles(() => ({
    root: {
        minHeight: '300px',
        marginLeft: 'calc(15% + 25px)',
        marginRight: '25px',
        padding: '20px',
    },
    pageTitle: {
        fontFamily: 'Rubik',
        fontWeight: 500,
        fontSize: '24px',
        color: CapeeshColors.DarkText,
    },
    pageDescription: {
        fontFamily: 'Rubik',
        fontWeight: 400,
        fontSize: '14px',
        color: CapeeshColors.Gray1,
        marginTop: '4px',
        marginBottom: '20px',
    },
    sectionTitle: {
        fontFamily: 'Rubik',
        fontWeight: 500,
        fontSize: '18px',
        color: CapeeshColors.DarkText,
    },
    sectionDescription: {
        fontFamily: 'Rubik',
        fontWeight: 400,
        fontSize: '14px',
        color: CapeeshColors.Gray1,
        marginTop: '8px',
        marginBottom: '16px',
        maxWidth: '760px',
    },
    buttonRow: {
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        marginBottom: '16px',
    },
    summaryText: {
        fontFamily: 'Rubik',
        fontWeight: 400,
        fontSize: '14px',
        color: CapeeshColors.DarkText,
        marginBottom: '12px',
    },
    truncatedText: {
        fontFamily: 'Rubik',
        fontWeight: 400,
        fontSize: '13px',
        color: CapeeshColors.OrangeBright,
        marginBottom: '12px',
    },
    tableHeaderCell: {
        fontSize: '14px',
        fontWeight: 500,
        fontFamily: 'Rubik',
        textAlign: 'left',
    },
    tableCell: {
        fontSize: '14px',
        fontWeight: 400,
        fontFamily: 'Rubik',
    },
    skipReasonCell: {
        fontSize: '13px',
        fontWeight: 400,
        fontFamily: 'Rubik',
        color: CapeeshColors.Gray1,
    },
    dialogText: {
        fontFamily: 'Rubik',
        fontSize: '14px',
    },
}))

const toPercentageString = (normalizedScore) => {
    if (normalizedScore === null || normalizedScore === undefined) {
        return '-'
    }
    return `${(normalizedScore * 100).toFixed(0)}%`
}

const formatDate = (dateString) => {
    if (!dateString) {
        return '-'
    }
    return new Date(dateString).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    })
}

const AdminUtilities = (props) => {
    const classes = useStyles()
    const intl = useIntl()

    const { organization, user } = props
    const organizationId = organization?.selectedOrganization

    const [result, setResult] = useState(null)
    const [loadingPreview, setLoadingPreview] = useState(false)
    const [applying, setApplying] = useState(false)
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

    const repairableRows = result?.rows?.filter((x) => x.canBeRepaired) || []

    const handlePreview = async () => {
        if (!organizationId) {
            setSnackbar({
                open: true,
                message: intl.formatMessage(i18nMessages.adminUtilitiesNoOrganization),
                severity: 'error',
            })
            return
        }

        setLoadingPreview(true)
        try {
            const response = await userService.previewWritingScoreRepair({ organizationId })
            setResult(response.data)
        } catch (error) {
            console.error('Failed to preview writing score repair', error)
            setSnackbar({
                open: true,
                message: intl.formatMessage(i18nMessages.adminUtilitiesLoadFailed),
                severity: 'error',
            })
        } finally {
            setLoadingPreview(false)
        }
    }

    const handleApply = async () => {
        setShowConfirmDialog(false)
        setApplying(true)
        try {
            const response = await userService.applyWritingScoreRepair({ organizationId })
            setResult(response.data)
            setSnackbar({
                open: true,
                message: intl.formatMessage(i18nMessages.adminUtilitiesAppliedSummary, {
                    applied: response.data?.appliedCount ?? 0,
                }),
                severity: 'success',
            })
        } catch (error) {
            console.error('Failed to apply writing score repair', error)
            setSnackbar({
                open: true,
                message: intl.formatMessage(i18nMessages.adminUtilitiesLoadFailed),
                severity: 'error',
            })
        } finally {
            setApplying(false)
        }
    }

    if (user?.isSuperAdmin !== true) {
        return (
            <div className={classes.root}>
                <OrganizationOverviewSidebar path={props.computedMatch?.path} />
            </div>
        )
    }

    return (
        <div className={classes.root}>
            <OrganizationOverviewSidebar path={props.computedMatch?.path} />

            <Typography className={classes.pageTitle}>
                {intl.formatMessage(i18nMessages.adminUtilitiesTitle)}
            </Typography>
            <Typography className={classes.pageDescription}>
                {intl.formatMessage(i18nMessages.adminUtilitiesDescription)}
            </Typography>

            <Card>
                <CardContent>
                    <Typography className={classes.sectionTitle}>
                        {intl.formatMessage(i18nMessages.adminUtilitiesWritingRepairTitle)}
                    </Typography>
                    <Typography className={classes.sectionDescription}>
                        {intl.formatMessage(i18nMessages.adminUtilitiesWritingRepairDescription)}
                    </Typography>

                    <div className={classes.buttonRow}>
                        <Button
                            variant="outlined"
                            color="primary"
                            disabled={loadingPreview || applying}
                            onClick={handlePreview}>
                            {intl.formatMessage(i18nMessages.adminUtilitiesPreview)}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={loadingPreview || applying || repairableRows.length === 0}
                            onClick={() => setShowConfirmDialog(true)}>
                            {intl.formatMessage(i18nMessages.adminUtilitiesRunMigration)}
                        </Button>
                        {(loadingPreview || applying) && <CircularProgress size={22} />}
                    </div>

                    {result && (
                        <React.Fragment>
                            <Typography className={classes.summaryText}>
                                {intl.formatMessage(i18nMessages.adminUtilitiesSummary, {
                                    total: result.totalCandidates ?? 0,
                                    repairable: result.repairableCount ?? 0,
                                    skipped: result.skippedCount ?? 0,
                                })}
                            </Typography>

                            {result.truncated && (
                                <Typography className={classes.truncatedText}>
                                    {intl.formatMessage(i18nMessages.adminUtilitiesTruncated, {
                                        shown: result.rows?.length ?? 0,
                                        total: result.totalCandidates ?? 0,
                                    })}
                                </Typography>
                            )}

                            {(result.rows?.length ?? 0) === 0 ? (
                                <Typography className={classes.summaryText}>
                                    {intl.formatMessage(i18nMessages.adminUtilitiesNoResults)}
                                </Typography>
                            ) : (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell className={classes.tableHeaderCell}>
                                                    {intl.formatMessage(i18nMessages.adminUtilitiesColumnStudent)}
                                                </TableCell>
                                                <TableCell className={classes.tableHeaderCell}>
                                                    {intl.formatMessage(i18nMessages.adminUtilitiesColumnLesson)}
                                                </TableCell>
                                                <TableCell className={classes.tableHeaderCell}>
                                                    {intl.formatMessage(i18nMessages.adminUtilitiesColumnCompleted)}
                                                </TableCell>
                                                <TableCell className={classes.tableHeaderCell}>
                                                    {intl.formatMessage(i18nMessages.adminUtilitiesColumnCurrentScore)}
                                                </TableCell>
                                                <TableCell className={classes.tableHeaderCell}>
                                                    {intl.formatMessage(i18nMessages.adminUtilitiesColumnWritingScore)}
                                                </TableCell>
                                                <TableCell className={classes.tableHeaderCell}>
                                                    {intl.formatMessage(i18nMessages.adminUtilitiesColumnNewScore)}
                                                </TableCell>
                                                <TableCell className={classes.tableHeaderCell}>
                                                    {intl.formatMessage(i18nMessages.adminUtilitiesColumnStars)}
                                                </TableCell>
                                                <TableCell className={classes.tableHeaderCell}>
                                                    {intl.formatMessage(i18nMessages.adminUtilitiesColumnStatus)}
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {result.rows.map((row) => (
                                                <TableRow key={row.customLessonReportId}>
                                                    <TableCell className={classes.tableCell}>
                                                        {row.userName || row.userEmail || row.userId}
                                                    </TableCell>
                                                    <TableCell className={classes.tableCell}>
                                                        {row.customLessonName}
                                                    </TableCell>
                                                    <TableCell className={classes.tableCell}>
                                                        {formatDate(row.completedAt)}
                                                    </TableCell>
                                                    <TableCell className={classes.tableCell}>
                                                        {toPercentageString(row.currentNormalizedScore)}
                                                    </TableCell>
                                                    <TableCell className={classes.tableCell}>
                                                        {row.writingAiNormalizedScores?.length > 0
                                                            ? row.writingAiNormalizedScores
                                                                  .map(toPercentageString)
                                                                  .join(', ')
                                                            : '-'}
                                                    </TableCell>
                                                    <TableCell className={classes.tableCell}>
                                                        {row.canBeRepaired
                                                            ? toPercentageString(row.newNormalizedScore)
                                                            : '-'}
                                                    </TableCell>
                                                    <TableCell className={classes.tableCell}>
                                                        {row.canBeRepaired
                                                            ? `${row.currentStars} → ${row.newStars}`
                                                            : row.currentStars}
                                                    </TableCell>
                                                    <TableCell
                                                        className={
                                                            row.canBeRepaired
                                                                ? classes.tableCell
                                                                : classes.skipReasonCell
                                                        }>
                                                        {row.canBeRepaired
                                                            ? row.applied
                                                                ? intl.formatMessage(
                                                                      i18nMessages.adminUtilitiesStatusApplied
                                                                  )
                                                                : intl.formatMessage(
                                                                      i18nMessages.adminUtilitiesStatusReady
                                                                  )
                                                            : row.skipReason}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </React.Fragment>
                    )}
                </CardContent>
            </Card>

            <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{intl.formatMessage(i18nMessages.adminUtilitiesConfirmTitle)}</DialogTitle>
                <DialogContent>
                    <Typography className={classes.dialogText}>
                        {intl.formatMessage(i18nMessages.adminUtilitiesConfirmBody, {
                            count: repairableRows.length,
                            organizationName: result?.organizationName || '',
                        })}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowConfirmDialog(false)}>
                        {intl.formatMessage(i18nMessages.adminUtilitiesCancel)}
                    </Button>
                    <Button variant="contained" color="primary" onClick={handleApply}>
                        {intl.formatMessage(i18nMessages.adminUtilitiesConfirm)}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    )
}

const mapStateToProps = (state) => {
    const { organization, user } = state
    return {
        organization,
        user,
    }
}

export default connect(mapStateToProps)(AdminUtilities)
